import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Text,
  FormControl,
  Input,
  VStack,
  Spinner,
  Checkbox,
  Collapse,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Image,
  Badge,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon, AddIcon, CheckIcon, MinusIcon } from '@chakra-ui/icons';
import api from '../api';
import ProductDetailModal from './ProductDetailModal';

const ProductSelectionModal = ({
  isOpen,
  onClose,
  onAddProduct,
  onRemoveProduct,
  selectedProducts = [],
  categories = [],
}) => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const itemBg = useColorModeValue('gray.50', 'gray.700');
  const selectedBg = useColorModeValue('green.50', 'green.900');
  const selectedHoverBg = useColorModeValue('green.100', 'green.800');
  const selectedBorderColor = useColorModeValue('#22C55E', '#16A34A');
  const selectedHoverBorderColor = useColorModeValue('#16A34A', '#15803D');

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({ name: '' });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  // Product detail modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductDetailOpen, setIsProductDetailOpen] = useState(false);
  const [isFullImageOpen, setIsFullImageOpen] = useState(false);
  const [fullImageSrc, setFullImageSrc] = useState('');

  const placeholderImage = '/placeholder-product.jpeg';

  useEffect(() => {
    if (isOpen) {
      fetchProducts(1);
    }
  }, [isOpen, filters.name, selectedCategories]);

  const fetchProducts = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      if (filters.name) params.append('search', filters.name);
      if (selectedCategories.length > 0) params.append('categories', selectedCategories.join(','));
      
      const response = await api.get(`/products/?${params.toString()}`);
      
      if (page === 1) {
        setProducts(response.data.results);
      } else {
        setProducts(prev => [...prev, ...response.data.results]);
      }
      
      setCurrentPage(page);
      setTotalPages(Math.ceil(response.data.count / 10));
      setHasMore(!!response.data.next);
      
      console.log('Fetched products:', {
        page,
        count: response.data.results.length,
        hasMore: !!response.data.next,
        total: response.data.count
      });
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreProducts = () => {
    if (!loading && hasMore) {
      fetchProducts(currentPage + 1);
    }
  };

  const handleCategoryChange = (categoryId, category) => {
    const collectChildCategoryIds = (cat) => {
      let ids = [cat.id];
      if (cat.children) {
        cat.children.forEach((child) => {
          ids = ids.concat(collectChildCategoryIds(child));
        });
      }
      return ids;
    };

    const childIds = collectChildCategoryIds(category);

    if (selectedCategories.some((id) => childIds.includes(id))) {
      setSelectedCategories((prev) => prev.filter((id) => !childIds.includes(id)));
    } else {
      setSelectedCategories((prev) => [...prev, ...childIds]);
    }
    
    // Reset to page 1 when changing filters
    setCurrentPage(1);
    setProducts([]);
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isCategorySelected = (category) => {
    const collectChildCategoryIds = (cat) => {
      let ids = [cat.id];
      if (cat.children) {
        cat.children.forEach((child) => {
          ids = ids.concat(collectChildCategoryIds(child));
        });
      }
      return ids;
    };

    const childIds = collectChildCategoryIds(category);
    const selectedChildren = childIds.filter((id) => selectedCategories.includes(id));

    if (selectedChildren.length === childIds.length) {
      return true;
    }
    if (selectedChildren.length > 0) {
      return 'indeterminate';
    }
    return false;
  };

  const renderCategories = (categories, depth = 0) =>
    categories.map((category) => {
      const categoryState = isCategorySelected(category);
      return (
        <Box key={category.id} mb="2" ml={depth * 4}>
          <Flex align="center">
            {category.children?.length > 0 && (
              <IconButton
                icon={
                  expandedCategories.includes(category.id) ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronRightIcon />
                  )
                }
                size="sm"
                onClick={() => toggleCategoryExpansion(category.id)}
                variant="ghost"
                aria-label="Toggle Category"
                mr="2"
              />
            )}
            <Checkbox
              isChecked={categoryState === true}
              isIndeterminate={categoryState === 'indeterminate'}
              onChange={() => handleCategoryChange(category.id, category)}
            >
              {category.name}
            </Checkbox>
          </Flex>
          {category.children?.length > 0 && (
            <Collapse in={expandedCategories.includes(category.id)} animateOpacity>
              {renderCategories(category.children, depth + 1)}
            </Collapse>
          )}
        </Box>
      );
    });

  const handleCloseModal = () => {
    // Reset state when closing
    setProducts([]);
    setCurrentPage(1);
    setFilters({ name: '' });
    setSelectedCategories([]);
    setExpandedCategories([]);
    setSelectedProduct(null);
    setIsProductDetailOpen(false);
    onClose();
  };
  
  const handleProductClick = (product, event) => {
    // Don't open detail modal if clicking on the add button
    if (event.target.closest('button')) {
      return;
    }
    setSelectedProduct(product);
    setIsProductDetailOpen(true);
  };

  const handleCloseProductDetail = () => {
    setSelectedProduct(null);
    setIsProductDetailOpen(false);
  };

  const openFullImage = (imageSrc) => {
    setFullImageSrc(imageSrc);
    setIsFullImageOpen(true);
  };

  const closeFullImage = () => {
    setIsFullImageOpen(false);
    setFullImageSrc('');
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const scrollThreshold = 200; // Load more when 200px from bottom
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;
    
    if (isNearBottom && hasMore && !loading) {
      loadMoreProducts();
    }
  };

  // Enhanced mouse wheel handling for the smoothest possible scrolling
  const handleWheel = (e) => {
    // Allow native wheel scrolling - browsers handle this optimally
    // Just stop propagation to prevent parent scroll interference
    e.stopPropagation();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleCloseModal} 
      size={{ base: 'full', md: '6xl' }} 
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
      <ModalContent 
        maxWidth={{ base: '100%', md: '95%' }} 
        h={{ base: '100vh', md: '90vh' }} 
        bg={cardBg}
        borderRadius={{ base: 0, md: '2xl' }}
        boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
        border="1px solid"
        borderColor={borderColor}
        position="relative"
        overflow="hidden"
      >
        {/* Product modal accent bar */}
        <Box position="absolute" top={0} left={0} right={0} height="6px" bg="linear-gradient(90deg, #4facfe, #00f2fe, #f093fb, #f5576c)" />
        
        <ModalHeader 
          color={textColor}
          py={{ base: 3, md: 6 }}
          px={{ base: 4, md: 8 }}
          fontSize={{ base: 'lg', md: '2xl' }}
          fontWeight="bold"
          bgGradient="linear(to-r, #4facfe, #00f2fe)"
          bgClip="text"
          mt={{ base: 1, md: 2 }}
        >
          🛍️ Обрати товари для накладної
        </ModalHeader>
        <ModalCloseButton color={textColor} size={{ base: 'md', md: 'lg' }} top={{ base: 3, md: 6 }} right={{ base: 4, md: 8 }} />
        
        <ModalBody p={0} h={{ base: 'calc(100% - 120px)', md: 'calc(100% - 140px)' }} overflow="hidden">
          <Flex h="100%" direction={{ base: 'column', md: 'row' }}>
            {/* Left Sidebar - Filters */}
            <Box
              flex={{ base: '0 0 auto', md: '0 0 320px' }}
              borderRight={{ base: 'none', md: '1px solid' }}
              borderColor={borderColor}
              p={{ base: 3, md: 6 }}
              bg={useColorModeValue('gray.50', 'gray.750')}
              maxH={{ base: '35vh', md: '100%' }}
              overflowY="auto"
            >
              <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={textColor} bgGradient="linear(to-r, #4facfe, #00f2fe)" bgClip="text" mb={{ base: 3, md: 6 }}>
                🔍 Фільтри
              </Text>
              {/* Search Filter */}
              <FormControl mb={{ base: 3, md: 6 }}>
                <Text fontSize="sm" fontWeight="600" color={textColor} mb={2}>Пошук</Text>
                <Input
                  placeholder="🔍 Пошук за назвою товару"
                  value={filters.name}
                  onChange={(e) => { setFilters({ ...filters, name: e.target.value }); setCurrentPage(1); setProducts([]); }}
                  bg={cardBg}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  size={{ base: 'sm', md: 'md' }}
                  _focus={{ borderColor: '#4facfe', boxShadow: '0 0 0 1px rgba(79, 172, 254, 0.3)' }}
                  _placeholder={{ color: subtleTextColor }}
                />
              </FormControl>
              {/* Category Filter */}
              <FormControl>
                <Text fontSize="sm" fontWeight="600" color={textColor} mb={2}>Категорії</Text>
                <Box bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor} p={4} maxH={{ base: '28vh', md: '400px' }} overflowY="auto">
                  {categories.length > 0 ? renderCategories(categories) : <Text color={subtleTextColor} fontSize="sm">Категорії не знайдено</Text>}
                </Box>
              </FormControl>
            </Box>

            {/* Right Side - Product List */}
            <Box flex="1" display="flex" flexDirection="column">
              <Box p={{ base: 3, md: 6 }} borderBottom="1px solid" borderColor={borderColor}>
                <Flex justify="space-between" align="center">
                  <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" color={textColor} bgGradient="linear(to-r, #f093fb, #f5576c)" bgClip="text">📦 Товари</Text>
                  <Text fontSize={{ base: 'xs', md: 'sm' }} color={subtleTextColor}>Сторінка {currentPage} з {totalPages}</Text>
                </Flex>
              </Box>
              <Box flex="1" overflowY="auto" p={{ base: 3, md: 4 }} onScroll={handleScroll} onWheel={handleWheel} maxH={{ base: 'calc(100vh - 280px)', md: 'calc(100vh - 300px)' }}>
                {products.length > 0 ? (
                  <VStack spacing={3} align="stretch">
                    {products.map((product) => (
                      <Box key={product.id} p={4} borderWidth="2px" borderColor={selectedProducts.includes(product.id) ? selectedBorderColor : borderColor} borderRadius="lg" bg={selectedProducts.includes(product.id) ? selectedBg : itemBg} _hover={{ bg: selectedProducts.includes(product.id) ? selectedHoverBg : hoverBg, borderColor: selectedProducts.includes(product.id) ? selectedHoverBorderColor : '#f093fb', transform: 'translateY(-1px)', boxShadow: `0 4px 8px ${shadowColor}` }} cursor="pointer" transition="all 0.2s" onClick={(e) => handleProductClick(product, e)}>
                        <Flex align="center" justify="space-between" gap={4}>
                          <Image src={product.images?.[0]?.image || placeholderImage} alt={product.name} boxSize={{ base: '52px', md: '60px' }} objectFit="cover" borderRadius="md" flexShrink={0} border="1px solid" borderColor={borderColor} />
                          <Box flex="1" minW="0">
                            <Text fontWeight="600" fontSize={{ base: 'sm', md: 'md' }} color={textColor} mb={2} lineHeight="1.2">{product.name}</Text>
                            <HStack spacing={3} mb={2}>
                              <Text fontSize={{ base: 'xs', md: 'sm' }} color={subtleTextColor}>💰 {product.price} грн</Text>
                              <Text fontSize={{ base: 'xs', md: 'sm' }} color={subtleTextColor}>📦 {product.stock_quantity} шт</Text>
                            </HStack>
                            <HStack spacing={2}>
                              <Badge colorScheme="blue" borderRadius="md" px={2} fontSize="xs">ID: {product.id}</Badge>
                              {product.catalog_number && (<Badge colorScheme="purple" borderRadius="md" px={2} fontSize="xs">{product.catalog_number}</Badge>)}
                              {selectedProducts.includes(product.id) && (<Badge colorScheme="green" borderRadius="md" px={2} fontSize="xs">✓ Обрано</Badge>)}
                            </HStack>
                          </Box>
                          <Box flexShrink={0}>
                            {selectedProducts.includes(product.id) ? (
                              <IconButton icon={<MinusIcon />} onClick={() => onRemoveProduct && onRemoveProduct(product.id)} colorScheme="red" size={{ base: 'md', md: 'lg' }} borderRadius="lg" _hover={{ transform: 'scale(1.1)' }} transition="all 0.2s" aria-label="Remove product" />
                            ) : (
                              <IconButton icon={<AddIcon />} onClick={() => onAddProduct(product)} colorScheme="blue" size={{ base: 'md', md: 'lg' }} borderRadius="lg" _hover={{ transform: 'scale(1.1)' }} transition="all 0.2s" aria-label="Add product" />
                            )}
                          </Box>
                        </Flex>
                      </Box>
                    ))}
                    {loading && (
                      <Box textAlign="center" py={6}>
                        <Spinner size="lg" color="blue.500" thickness="3px" />
                        <Text fontSize="sm" color={subtleTextColor} mt={2}>Завантаження товарів...</Text>
                      </Box>
                    )}
                    {!loading && hasMore && (
                      <Button onClick={loadMoreProducts} variant="outline" colorScheme="blue" size={{ base: 'md', md: 'lg' }} w="100%" mt={4}>
                        Завантажити ще
                      </Button>
                    )}
                  </VStack>
                ) : (
                  <Box textAlign="center" py={{ base: 8, md: 12 }} color={subtleTextColor}>
                    {loading ? (
                      <>
                        <Spinner size="xl" color="blue.500" thickness="3px" mb={4} />
                        <Text fontSize={{ base: 'md', md: 'lg' }}>Завантаження товарів...</Text>
                      </>
                    ) : (
                      <>
                        <Text fontSize={{ base: 'xl', md: '2xl' }} mb={2}>📭</Text>
                        <Text fontSize={{ base: 'md', md: 'lg' }}>Товарів не знайдено</Text>
                        <Text fontSize={{ base: 'sm', md: 'sm' }} mt={2}>Спробуйте змінити фільтри пошуку</Text>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Flex>
        </ModalBody>
        
        <ModalFooter borderTop="1px solid" borderColor={borderColor} py={{ base: 3, md: 4 }} px={{ base: 4, md: 8 }} bg={useColorModeValue('gray.50', 'gray.700')} flexShrink={0}>
          <Button 
            bg="linear-gradient(135deg, #4facfe, #00f2fe)"
            color="white"
            onClick={handleCloseModal}
            borderRadius="lg"
            px={{ base: 6, md: 8 }}
            size={{ base: 'md', md: 'lg' }}
            boxShadow="0 4px 15px rgba(79, 172, 254, 0.4)"
            _hover={{ bg: 'linear-gradient(135deg, #3b9cfe, #00d9fe)', transform: { base: 'none', md: 'translateY(-2px)' }, boxShadow: '0 8px 25px rgba(79, 172, 254, 0.6)' }}
            _active={{ transform: 'translateY(0)' }}
            transition="all 0.2s"
          >
            ✅ Готово
          </Button>
        </ModalFooter>
      </ModalContent>
      
      {/* Product Detail Modal */}
      <ProductDetailModal isOpen={isProductDetailOpen} onClose={handleCloseProductDetail} product={selectedProduct} onOpenFullImage={openFullImage} />
      {/* Full Image Modal unchanged */}
      <Modal isOpen={isFullImageOpen} onClose={closeFullImage} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" onClick={closeFullImage} />
        <ModalContent bg="transparent" boxShadow="none" maxW="95vw" maxH="95vh" position="relative">
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" position="relative" onClick={closeFullImage}>
            <Box position="relative" maxW="100%" maxH="100%" onClick={(e) => e.stopPropagation()}>
              <Image src={fullImageSrc} alt="Full size" maxW="100%" maxH="95vh" objectFit="contain" borderRadius="lg" onClick={closeFullImage} cursor="pointer" />
              <IconButton icon={<Text fontSize="lg">✕</Text>} aria-label="Close full image" position="absolute" top="10px" right="10px" colorScheme="whiteAlpha" bg="blackAlpha.600" color="white" size="lg" borderRadius="full" onClick={closeFullImage} _hover={{ bg: 'blackAlpha.800' }} />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Modal>
  );
};

export default ProductSelectionModal;
